import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, CardView, SCROLL_INDICATOR_INSETS, ButtonGroup, Spacer, } from 'design-system';
import { hitSlop } from 'design-system/values';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Section from 'design-system/Section';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectGroupAnalytics, selectGroupById, selectUser, } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { sendGroupSettlementReminder } from 'actions';
import links from 'utils/links';
import { useTimeout } from 'hooks';
import iconSource from 'design-system/img/notifications.png';
import { SettleUpModal } from './components/SettleUpModal';
import { GroupExpenseCRowSelectable } from './components/GroupExpenseCRow';
const strings = {
    title: 'Select a debt you want to settle up',
    subtitle: 'See who owes you and what you owe at a glance. We automatically calculate the smartest way to settle up with your group',
    shortTitle: 'Settle up',
};
const GroupSettleUpScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { groupId } = route.params;
    const dispatch = useAppDispatch();
    const [selected, setSelected] = useState();
    const [showToast, setShowToast] = useState(false);
    useTimeout(() => setShowToast(false), showToast ? 3000 : undefined);
    const onRemind = useCallback(async () => {
        if (selected?.handle) {
            SettleUpModal.hide();
            dispatch(sendGroupSettlementReminder(groupId, selected?.handle));
            setShowToast(true);
        }
    }, [groupId, selected?.handle]);
    const user = useAppSelector(selectUser);
    const group = useAppSelector((store) => selectGroupById(store, groupId));
    const groupAnalytics = useAppSelector((store) => selectGroupAnalytics(store, groupId));
    const owesToYou = useMemo(() => groupAnalytics?.users.filter((u) => u.myBalance < 0), [groupAnalytics?.users]);
    const youOwe = useMemo(() => groupAnalytics?.users.filter((u) => u.myBalance > 0), [groupAnalytics?.users]);
    const showOptionsModal = useCallback(() => {
        if (!selected || !group)
            return;
        const [payer, payee] = selected.myBalance < 0 ? [selected, user] : [user, selected];
        SettleUpModal.show({
            group,
            payer,
            payee,
            navigation,
            onRemind,
            fullAmount: selected.myBalance,
        });
    }, [group, onRemind, selected, user]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<>
        <PaymentToastModal isVisible={showToast} onCloseAction={() => setShowToast(false)} title="Sent a reminder to settle up" message={`To ${selected?.firstName || selected?.handle}`} iconSource={iconSource} style={styles.toast}/>
        <AppView style={styles.container} withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom}>
            <ScreenInfoHeader title={strings.title} subTitle={<>
                  <Text TextThin-16 Secondary>
                    {strings.subtitle}
                  </Text>
                  <Spacer.H8 />
                  <TouchableOpacity hitSlop={hitSlop} onPress={links.openGroupSettleUp}>
                    <Text Text-16 Primary>
                      How is it calculated?
                    </Text>
                  </TouchableOpacity>
                </>}/>

            <Spacer.H8 />

            {owesToYou && owesToYou.length > 0 ? (<Section title="Owes to you">
                <CardView noMarginTop>
                  {owesToYou.map((groupUser) => (<GroupExpenseCRowSelectable key={groupUser.id} groupUser={groupUser} onPress={setSelected} selected={selected?.id === groupUser.id} balance={-groupUser.myBalance} currency={groupAnalytics?.currency} isMultipleSelect={false}/>))}
                </CardView>
              </Section>) : null}
            {youOwe && youOwe.length > 0 ? (<Section title="You owe">
                <CardView noMarginTop>
                  {youOwe.map((groupUser) => (<GroupExpenseCRowSelectable key={groupUser.id} groupUser={groupUser} onPress={setSelected} selected={selected?.id === groupUser.id} balance={-groupUser.myBalance} currency={groupAnalytics?.currency} isMultipleSelect={false} showMinus={false}/>))}
                </CardView>
              </Section>) : null}
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brand onPress={showOptionsModal} disabled={!selected} title="Settle up"/>
          </ButtonGroup>
        </AppView>
      </>);
}, ({ colors }) => ({
    title: strings.shortTitle,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    toast: {
        paddingTop: 0,
    },
});
export default GroupSettleUpScreen;
